// src/components/FCMDebug.js
import React, { useState } from 'react';
import { requestForToken, registerServiceWorker, isFCMSupported } from '../firebase';
import { toast } from 'react-hot-toast';

const FCMDebug = () => {
  const [debugSteps, setDebugSteps] = useState([]);
  const [token, setToken] = useState('');
  const [isTesting, setIsTesting] = useState(false);

  const runFCMTests = async () => {
    setIsTesting(true);
    const steps = [];
    
    try {
      // Step 1: Check secure context
      if (!window.isSecureContext && window.location.hostname !== 'localhost') {
        steps.push('❌ Not in secure context (HTTPS required for FCM)');
        toast.error('HTTPS required for FCM');
      } else {
        steps.push('✅ Secure context check passed');
      }

      // Step 2: Check service worker support
      if (!('serviceWorker' in navigator)) {
        steps.push('❌ Service Worker not supported in this browser');
        toast.error('Service Worker not supported');
      } else {
        steps.push('✅ Service Worker supported');
      }

      // Step 3: Check notification support
      if (!('Notification' in window)) {
        steps.push('❌ Notifications not supported');
        toast.error('Notifications not supported');
      } else {
        steps.push('✅ Notifications supported');
        
        // Check current permission
        const permission = Notification.permission;
        steps.push(`ℹ️ Current notification permission: ${permission}`);
      }

      // Step 4: Register service worker
      steps.push('🔄 Attempting to register service worker...');
      const registration = await registerServiceWorker();
      if (registration) {
        steps.push(`✅ Service Worker registered with scope: ${registration.scope}`);
      } else {
        steps.push('❌ Service Worker registration failed');
      }

      // Step 5: Test FCM support
      steps.push('🔄 Checking FCM support...');
      const isSupported = await isFCMSupported();
      if (isSupported) {
        steps.push('✅ FCM is supported in this browser');
      } else {
        steps.push('❌ FCM is not supported in this browser');
      }

      // Step 6: Try to get token
      if (isSupported) {
        steps.push('🔄 Attempting to get FCM token...');
        const garageId = localStorage.getItem('grage_id') || 'test123';
        const companyId = localStorage.getItem('company_id') || '2';
        
        const fcmToken = await requestForToken(garageId, companyId);
        if (fcmToken) {
          steps.push(`✅ FCM token obtained: ${fcmToken.substring(0, 30)}...`);
          setToken(fcmToken);
          toast.success('FCM token generated successfully!');
        } else {
          steps.push('❌ Failed to get FCM token');
          toast.error('Failed to get FCM token');
        }
      }

    } catch (error) {
      steps.push(`💥 Error: ${error.message}`);
      toast.error(`Error: ${error.message}`);
    }

    setDebugSteps(steps);
    setIsTesting(false);
  };

  const copyToken = () => {
    if (token) {
      navigator.clipboard.writeText(token);
      toast.success('Token copied to clipboard!');
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1>FCM Debug Tool</h1>
      
      <button 
        onClick={runFCMTests}
        disabled={isTesting}
        style={{
          padding: '10px 20px',
          backgroundColor: '#0352a1',
          color: 'white',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer',
          marginBottom: '20px'
        }}
      >
        {isTesting ? 'Testing...' : 'Run FCM Tests'}
      </button>

      {token && (
        <div style={{ marginBottom: '20px' }}>
          <h3>FCM Token:</h3>
          <div style={{
            backgroundColor: '#f5f5f5',
            padding: '10px',
            borderRadius: '5px',
            wordBreak: 'break-all',
            marginBottom: '10px'
          }}>
            {token}
          </div>
          <button 
            onClick={copyToken}
            style={{
              padding: '5px 10px',
              backgroundColor: '#48bb78',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer'
            }}
          >
            Copy Token
          </button>
        </div>
      )}

      <h3>Debug Steps:</h3>
      <div style={{
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '5px',
        padding: '15px'
      }}>
        {debugSteps.length === 0 ? (
          <p>Click "Run FCM Tests" to start debugging</p>
        ) : (
          <ol style={{ paddingLeft: '20px' }}>
            {debugSteps.map((step, index) => (
              <li key={index} style={{ marginBottom: '8px' }}>
                {step}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
};

export default FCMDebug;